import * as React from "react";
import { useEffect, useMemo, useState } from "react";
import { createPortal } from "../../windowManager/browserFenster/fensterPortal";
import "./vorgangV4.css";
import { alleFenster } from "../../windowManager/browserFenster/fensterRegister";

/**
 * Die durchsuchbare Liste des Vorgangs — einmal als Dialog, einmal als
 * Abschnitt.
 *
 * ══ WARUM ZWEI HÜLLEN ══════════════════════════════════════════════════════
 * Ursprünglich (Zeichnung 10.09.2026) lag die vollständige Liste hinter „Alle
 * Mails" und „Alle Dokumente" in der Aktionsleiste. Der PO hat das am
 * 11.09.2026 zurückgewiesen: die Liste gehört als ARCHIV ans Seitenende, quer
 * über alle To-dos („Das muss unten runter, unter alle To-dos, und da ist
 * alles von allen To-dos drin.", 17:43).
 *
 * Geblieben ist das Innere — Suchfeld plus Zeilenliste — als
 * {@link ListenFlaeche}. Der {@link AlleListeDialog} legt Schleier, ESC und
 * Klick-daneben darum, der `ArchivAbschnitt` eine aufklappbare Karte in der
 * Seite. ESC und Portal gehören NICHT in die Fläche: der Abschnitt steht in
 * der Seite, nicht darüber.
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * Bewusst KEIN Fluent-Dialog (PO 09.09.2026): eigene Schicht, eigene Farben.
 * Er rendert per Portal an `document.body` und bringt darum seine eigene
 * `.kvp-rd`-Hülle mit — außerhalb der Seite gälte sonst keine Farbvariable.
 */

export type ListenZeile = {
    key: string;
    titel: string;
    unterzeile: string;
    /** Rechts außen — immer dieselbe Stelle, wie in der alten Zeitachse. */
    wann: string;
    ikone: React.ReactNode;
    /** Worüber die Suche läuft; der Aufrufer entscheidet, was dazugehört. */
    suchtext: string;
    /**
     * Kleiner Hinweis links vor dem Zeitpunkt — im Archiv das To-do, aus dem
     * die Zeile stammt („ich kann ja 20 To-dos da drin haben", PO 11.09.2026).
     */
    badge?: React.ReactNode;
    onOeffnen?: () => void;
};

/** Die Suche: Freitext, ohne Groß-/Kleinschreibung, über `suchtext`. */
export function filtereZeilen<T extends { suchtext: string }>(zeilen: T[], suche: string): T[] {
    const q = suche.trim().toLowerCase();
    if (!q) return zeilen;
    // Mehrere Wörter = UND. „mauel angebot" soll die Mail von Frau Mauel über
    // das Angebot finden, auch wenn die Wörter weit auseinanderstehen.
    const teile = q.split(/\s+/);
    return zeilen.filter((z) => {
        const t = z.suchtext.toLowerCase();
        return teile.every((w) => t.includes(w));
    });
}

/**
 * Suchfeld + Zeilenliste. Die Suche lebt HIER, damit sie beim Umschalten der
 * Filterchips stehen bleibt; die Chips filtern `zeilen`, bevor sie ankommen.
 */
export const ListenFlaeche: React.FC<{
    zeilen: ListenZeile[];
    leerHinweis: string;
    suchPlatzhalter: string;
    /** Links vor dem Suchfeld (Dialog: Titel und Anzahl). */
    kopfVorn?: React.ReactNode;
    /** Rechts nach dem Suchfeld (Dialog: das Schließen-Kreuz). */
    kopfHinten?: React.ReactNode;
    /**
     * Neben dem Suchfeld, in DERSELBEN Zeile (Archiv: die Filterchips).
     * Die Zeile bricht um, wenn der Platz nicht reicht — sie ist damit weiter
     * eine eigene Zeile, wo es eng wird, aber keine zweite, wo es passt.
     */
    unterKopf?: React.ReactNode;
    /**
     * Vorn IM Suchfeld (Archiv: die Lupe). Ohne sie bleibt das Feld das
     * schlichte Eingabefeld.
     */
    suchIkone?: React.ReactNode;
    /** Nach dem Öffnen einer Zeile — der Dialog schließt sich damit. */
    onNachOeffnen?: () => void;
}> = ({
    zeilen,
    leerHinweis,
    suchPlatzhalter,
    kopfVorn,
    kopfHinten,
    unterKopf,
    suchIkone,
    onNachOeffnen,
}) => {
    const [suche, setSuche] = useState("");
    const treffer = useMemo(() => filtereZeilen(zeilen, suche), [zeilen, suche]);

    return (
        <>
            <div className="vg4-liste-kopf">
                {kopfVorn}
                {/* Die Hülle trägt im Archiv den Rahmen, damit die Lupe INNEN
                    steht; im Dialog ist sie durchsichtig und der Rahmen bleibt
                    am Eingabefeld — dort ändert sich damit nichts. */}
                <label className="vg4-such-feld">
                    {suchIkone}
                    <input
                        className="vg4-liste-such"
                        value={suche}
                        placeholder={suchPlatzhalter}
                        aria-label={suchPlatzhalter}
                        onChange={(e) => setSuche(e.target.value)}
                    />
                </label>
                {unterKopf}
                {kopfHinten}
            </div>

            <div className="vg4-liste-b">
                {treffer.length === 0 ? (
                    <div className="vg4-liste-leer">{suche.trim() ? "Keine Treffer." : leerHinweis}</div>
                ) : (
                    treffer.map((z) => {
                        const inhalt = (
                            <>
                                {z.ikone}
                                <span className="vg4-karte-b">
                                    <span className="vg4-lz-t">{z.titel}</span>
                                    <span className="vg4-lz-s">{z.unterzeile}</span>
                                </span>
                                {z.badge}
                                <span className="vg4-lz-wann">{z.wann}</span>
                            </>
                        );
                        // Ein Knopf, der nichts tut, ist schlimmer als ein
                        // Absatz (dieselbe Regel wie in `EreignisKarte`):
                        // Anrufe und Notizen haben kein Ziel zum Öffnen.
                        return z.onOeffnen ? (
                            <button
                                key={z.key}
                                type="button"
                                className="vg4-lz"
                                onClick={() => {
                                    z.onOeffnen?.();
                                    onNachOeffnen?.();
                                }}
                            >
                                {inhalt}
                            </button>
                        ) : (
                            <div key={z.key} className="vg4-lz statisch">
                                {inhalt}
                            </div>
                        );
                    })
                )}
            </div>
        </>
    );
};

export const AlleListeDialog: React.FC<{
    titel: string;
    zeilen: ListenZeile[];
    leerHinweis: string;
    suchPlatzhalter: string;
    onSchliessen: () => void;
}> = ({ titel, zeilen, leerHinweis, suchPlatzhalter, onSchliessen }) => {
    useEffect(() => {
        const auf = (e: KeyboardEvent) => {
            if (e.key === "Escape") onSchliessen();
        };
        alleFenster.addEventListener("keydown", auf);
        return () => alleFenster.removeEventListener("keydown", auf);
    }, [onSchliessen]);

    return createPortal(
        <div
            className="vg4-schleier"
            role="presentation"
            onMouseDown={(e) => {
                // Nur der Klick DANEBEN schließt — ein Klick in den Dialog,
                // der zufällig bis hierher durchblubbert, nicht.
                if (e.target === e.currentTarget) onSchliessen();
            }}
        >
            <div className="kvp-rd vg4-dialog" role="dialog" aria-modal="true" aria-label={titel}>
                <ListenFlaeche
                    zeilen={zeilen}
                    leerHinweis={leerHinweis}
                    suchPlatzhalter={suchPlatzhalter}
                    kopfVorn={
                        <>
                            <span className="vg4-dialog-t">{titel}</span>
                            <span className="vg4-todo-zahl">{zeilen.length}</span>
                        </>
                    }
                    kopfHinten={
                        <button
                            type="button"
                            className="vg4-dialog-x"
                            aria-label="Schließen"
                            onClick={onSchliessen}
                        >
                            ✕
                        </button>
                    }
                    onNachOeffnen={onSchliessen}
                />
            </div>
        </div>,
        document.body,
    );
};
